import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useMusic } from '../contexts/MusicContext';
import { Button } from './ui/button';
import { Check, X, Music, Clock, Inbox, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact pending-requests inbox for the DJ Console.
 * Renders ABOVE the decks. Polls every 3s for new requests.
 * - `pending` (manual approve) → Approve button injects via injectNext()
 * - `approved` + `auto_accepted:true` → auto-queued badge; refreshes the
 *   visible queue so the DJ sees the injected track appear in the "up next"
 *   panel without waiting for the crossfade.
 */
const RequestsInbox = () => {
  const { injectNext, refreshQueue } = useMusic();
  const [session, setSession] = useState(null);
  const [requests, setRequests] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const pollRef = useRef(null);
  // Set of request ids we've already surfaced as "auto-queued" — used to
  // avoid double-firing the toast + refresh across poll ticks.
  const seenAutoIdsRef = useRef(new Set());

  const fetchSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/requests/sessions/active`);
      setSession(res.data || null);
      return res.data;
    } catch {
      setSession(null);
      return null;
    }
  }, []);

  const fetchRequests = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const res = await axios.get(`${API}/requests/sessions/${sid}/requests`);
      const list = res.data || [];
      setRequests(list);
      // Detect newly-arrived auto-accepted requests. When one lands, the
      // backend has already injected it into the playback queue — but the
      // DJ Console's visible queue is React state that only refreshes on
      // crossfade / manual actions. Without this poll-side refresh, the
      // DJ sees "Guest submitted" everywhere except in the up-next panel,
      // which was the exact production complaint.
      const freshAuto = list.filter(
        (r) => r.auto_accepted && r.status === 'approved' && !seenAutoIdsRef.current.has(r.id)
      );
      if (freshAuto.length > 0) {
        freshAuto.forEach((r) => seenAutoIdsRef.current.add(r.id));
        // Trigger a queue refresh so the DJ sees the injected track appear.
        if (refreshQueue) refreshQueue().catch(() => {});
        // Announce the newest one (unobtrusive, non-blocking).
        const latest = freshAuto[0];
        toast.success(
          `${(latest.guest_name || 'Guest').slice(0, 20)} auto-queued: ${(latest.track?.name || 'a track').slice(0, 40)}`,
          { duration: 3500 }
        );
      }
    } catch (fetchErr) {
      console.warn('[RequestsInbox] fetch failed:', fetchErr?.response?.status, fetchErr?.message || fetchErr);
    }
  }, [refreshQueue]);

  useEffect(() => {
    (async () => {
      const s = await fetchSession();
      if (s?.id) {
        // Seed the "seen" set with the current auto-accepted requests so
        // we don't toast every historical auto-accept on first mount.
        try {
          const res = await axios.get(`${API}/requests/sessions/${s.id}/requests`);
          const list = res.data || [];
          list.forEach((r) => {
            if (r.auto_accepted && r.status === 'approved') {
              seenAutoIdsRef.current.add(r.id);
            }
          });
          setRequests(list);
        } catch {
          await fetchRequests(s.id);
        }
      }
    })();
  }, [fetchSession, fetchRequests]);

  useEffect(() => {
    if (!session?.id) return undefined;
    pollRef.current = setInterval(() => fetchRequests(session.id), 3000);
    return () => clearInterval(pollRef.current);
  }, [session?.id, fetchRequests]);

  const pending = requests.filter((r) => r.status === 'pending');
  // Show the last 5 auto-queued in a dim secondary row so the DJ has
  // continuous visibility that guest-submitted songs actually landed.
  // Excludes tracks that have already been played (backend marks them
  // `played` when they finish — the DJ Console's `handleTrackEnded` fires
  // a `/requests/mark-played` POST to clear them from this list).
  // Also excludes any auto-approved row older than 24h — a defence-in-depth
  // filter alongside the server-side stale sweep in get_session_requests
  // (the signal to mark 'played' can miss on manual skips / restarts).
  const AUTO_STALE_MS = 24 * 60 * 60 * 1000;
  const recentAuto = requests
    .filter((r) => {
      if (!r.auto_accepted || r.status !== 'approved') return false;
      const ts = Date.parse(r.created_at || '');
      if (!Number.isNaN(ts) && Date.now() - ts > AUTO_STALE_MS) return false;
      return true;
    })
    .slice(0, 5);

  const handleApprove = async (req) => {
    setBusyId(req.id);
    try {
      await axios.put(`${API}/requests/${req.id}/approve`);
      setRequests((prev) => prev.map((r) => (r.id === req.id ? { ...r, status: 'approved' } : r)));
      if (req.track) await injectNext(req.track);
      toast.success('Approved — queued next');
    } catch {
      toast.error('Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (req) => {
    setBusyId(req.id);
    try {
      await axios.put(`${API}/requests/${req.id}/reject`);
      setRequests((prev) => prev.map((r) => (r.id === req.id ? { ...r, status: 'rejected' } : r)));
    } catch {
      toast.error('Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  // Hidden when no live session at all.
  if (!session) return null;

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-3 lg:p-4 backdrop-blur-xl"
      data-testid="dj-requests-inbox"
    >
      <div className="flex items-center justify-between mb-2 lg:mb-3">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 lg:w-5 lg:h-5 text-[#FF007F]" />
          <h3 className="text-xs lg:text-sm font-extrabold text-white uppercase tracking-[0.18em]" style={{ fontFamily: 'Outfit' }}>
            Guest Requests
          </h3>
          {pending.length > 0 && (
            <span className="bg-[#FF007F] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="inbox-pending-count">
              {pending.length}
            </span>
          )}
          <span className="hidden sm:flex items-center gap-1 ml-2 text-[10px] text-[#39FF14] font-semibold tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
            LIVE
          </span>
        </div>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-gray-400 hover:text-white p-1 rounded transition-colors"
          data-testid="inbox-toggle-collapse"
          aria-label={collapsed ? 'Expand inbox' : 'Collapse inbox'}
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {!collapsed && (
        pending.length === 0 ? (
          <>
            <div className="flex items-center gap-3 py-2 lg:py-3 text-gray-500 text-xs lg:text-sm">
              <Clock className="w-4 h-4" />
              No pending requests — relax and mix.
            </div>
            {recentAuto.length > 0 && (
              <div className="mt-1 border-t border-white/5 pt-2" data-testid="inbox-recent-auto">
                <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500 mb-1.5 flex items-center gap-1">
                  <Zap className="w-3 h-3 text-[#39FF14]" /> Auto-queued (last {recentAuto.length})
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 hide-scrollbar">
                  {recentAuto.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-2 bg-black/30 border border-[#39FF14]/20 rounded-lg px-2 py-1.5 flex-shrink-0 min-w-[220px]"
                      data-testid={`inbox-auto-${r.id}`}
                    >
                      {r.track?.album_image ? (
                        <img src={r.track.album_image} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                          <Music className="w-3 h-3 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-300 text-[11px] font-semibold truncate">{r.track?.name || 'Unknown'}</p>
                        <p className="text-[#39FF14]/80 text-[9px] truncate">from {r.guest_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex gap-2 lg:gap-3 overflow-x-auto pb-1 -mx-1 px-1 hide-scrollbar">
            {pending.map((req) => (
              <div
                key={req.id}
                className="flex items-center gap-2 lg:gap-3 bg-black/40 border border-white/10 rounded-xl p-2 lg:p-3 flex-shrink-0 min-w-[260px] lg:min-w-[320px]"
                data-testid={`inbox-request-${req.id}`}
              >
                {req.track?.album_image ? (
                  <img src={req.track.album_image} alt="" className="w-10 h-10 lg:w-12 lg:h-12 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Music className="w-4 h-4 text-gray-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs lg:text-sm font-semibold truncate">{req.track?.name || 'Unknown'}</p>
                  <p className="text-gray-400 text-[10px] lg:text-xs truncate">
                    {Array.isArray(req.track?.artists) ? req.track.artists.join(', ') : ''}
                  </p>
                  <p className="text-[#FF007F] text-[10px] font-semibold truncate">{req.guest_name}</p>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <Button
                    onClick={() => handleApprove(req)}
                    disabled={busyId === req.id}
                    className="bg-[#39FF14] hover:bg-[#2dd60f] text-black h-7 w-7 lg:h-8 lg:w-8 p-0 rounded-full"
                    title="Approve & queue next"
                    data-testid={`inbox-approve-${req.id}`}
                  >
                    <Check className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                  </Button>
                  <Button
                    onClick={() => handleReject(req)}
                    disabled={busyId === req.id}
                    variant="ghost"
                    className="text-[#FF3B3B] hover:bg-[#FF3B3B]/10 h-7 w-7 lg:h-8 lg:w-8 p-0 rounded-full"
                    title="Reject"
                    data-testid={`inbox-reject-${req.id}`}
                  >
                    <X className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default RequestsInbox;
